import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getFieldValue, getAssetUrl, extractRichTextFromFields, renderRichText } from '../../../contentful/mappers';
import './Committee.css';

function Committee({ data }) {
  const [selectedMember, setSelectedMember] = useState(null);

  // Disable body scroll when overlay is open
  useEffect(() => {
    if (selectedMember) {
      // Save current overflow style
      const originalOverflow = document.body.style.overflow;
      // Disable scrolling
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore original overflow when overlay closes
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [selectedMember]);

  if (!data) return null;

  const headline = getFieldValue(data, 'headline');
  const members = getFieldValue(data, 'members') || [];
  const bgTheme = getFieldValue(data, 'bgTheme') || 'light';

  const sectionClass = `committee-section committee-section-${bgTheme}`;

  const handleMemberClick = (member) => {
    setSelectedMember(member);
  };

  const handleCloseOverlay = () => {
    setSelectedMember(null);
  };

  return (
    <section className={sectionClass}>
      <div className="container">
        {headline && <h2 className="committee-section-headline">{headline}</h2>}
        <div className="committee-grid">
          {members.map((member, index) => {
            const name = getFieldValue(member, 'name');
            const roleTitle = getFieldValue(member, 'roleTitle');
            const email = getFieldValue(member, 'email');
            const preferredPronouns = getFieldValue(member, 'preferredPronouns');
            const displayName = name ? name.toUpperCase() : '';
            const pronounsDisplay = preferredPronouns
              ? String(preferredPronouns).toLowerCase()
              : '';

            // Determine if member has a bio/description
            const bioForCard =
              extractRichTextFromFields(member, 'bio') ||
              extractRichTextFromFields(member, 'description');
            const hasBio = !!bioForCard;
            
            // Get photo - need to properly extract from locale object
            let photoUrl = null;
            
            if (member && member.fields && member.fields.photo) {
              const photoField = member.fields.photo;
              
              // Check if it's a locale object (has locale keys like 'en-US')
              if (typeof photoField === 'object' && photoField !== null && !Array.isArray(photoField)) {
                // Check if it has 'en-US' key (multi-locale format)
                if (photoField['en-US'] && photoField['en-US'].fields) {
                  photoUrl = getAssetUrl(photoField['en-US']);
                } 
                // Check if it's already the asset object (has fields.file directly)
                else if (photoField.fields && photoField.fields.file) {
                  photoUrl = getAssetUrl(photoField);
                }
                // Otherwise, try to find a locale key that has fields
                else {
                  const localeKeys = Object.keys(photoField).filter(key => {
                    const value = photoField[key];
                    return value && typeof value === 'object' && value.fields && value.fields.file;
                  });
                  if (localeKeys.length > 0) {
                    photoUrl = getAssetUrl(photoField[localeKeys[0]]);
                  }
                }
              } 
              // If it's already the asset object directly
              else if (photoField && photoField.fields && photoField.fields.file) {
                photoUrl = getAssetUrl(photoField);
              }
            }
          
            return (
              <div
                key={member.sys?.id || index}
                className={`committee-member-card${hasBio ? ' has-bio' : ' no-bio'}`}
                onClick={hasBio ? () => handleMemberClick(member) : undefined}
              >
                <div className="member-photo-wrapper">
                  {photoUrl ? (
                    <img 
                      src={photoUrl} 
                      alt={name} 
                      className="member-photo"
                      onError={(e) => {
                        console.error('Failed to load image:', photoUrl, 'for member:', name);
                        e.target.style.display = 'none';
                        e.target.parentElement.style.backgroundColor = '#f0f0f0';
                      }}
                      onLoad={() => {
                        // console.log('Image loaded successfully:', photoUrl);
                      }}
                    />
                  ) : (
                    <div className="member-photo-placeholder">
                      <span>No Photo</span>
                    </div>
                  )}
                </div>
                <div className="member-info">
                  <h3 className="member-name">
                    {displayName}
                    {pronounsDisplay && (
                      <span className="member-pronouns"> ({pronounsDisplay})</span>
                    )}
                  </h3>
                  {roleTitle && <p className="member-role">{roleTitle}</p>}
                  {email && <p className="member-email">{email}</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedMember && createPortal(
        <div className="overlay" onClick={handleCloseOverlay}>
          <div className="overlay-content" onClick={(e) => e.stopPropagation()}>
            <button className="overlay-close" onClick={handleCloseOverlay}>
              <span>close</span>
              <span className="close-icon">×</span>
            </button>
            
            <div className="overlay-main">
              <div className="overlay-left">
                {(() => {
                  // Get photo - same logic as above
                  let photoUrl = null;
                  
                  if (selectedMember && selectedMember.fields && selectedMember.fields.photo) {
                    const photoField = selectedMember.fields.photo;
                    
                    // Check if it's a locale object
                    if (typeof photoField === 'object' && photoField !== null && !Array.isArray(photoField)) {
                      // Check if it has 'en-US' key
                      if (photoField['en-US'] && photoField['en-US'].fields) {
                        photoUrl = getAssetUrl(photoField['en-US']);
                      } 
                      // Check if it's already the asset object
                      else if (photoField.fields && photoField.fields.file) {
                        photoUrl = getAssetUrl(photoField);
                      }
                      // Otherwise, try to find a locale key that has fields
                      else {
                        const localeKeys = Object.keys(photoField).filter(key => {
                          const value = photoField[key];
                          return value && typeof value === 'object' && value.fields && value.fields.file;
                        });
                        if (localeKeys.length > 0) {
                          photoUrl = getAssetUrl(photoField[localeKeys[0]]);
                        }
                      }
                    } 
                    // If it's already the asset object directly
                    else if (photoField && photoField.fields && photoField.fields.file) {
                      photoUrl = getAssetUrl(photoField);
                    }
                  }
                  
                  const name = getFieldValue(selectedMember, 'name');
                  
                  return (
                    photoUrl ? (
                      <img 
                        src={photoUrl} 
                        alt={name} 
                        className="overlay-photo"
                        onError={(e) => {
                          console.error('Failed to load overlay image:', photoUrl);
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : null
                  );
                })()}
              </div>
              
              <div className="overlay-right">
                {(() => {
                  const name = getFieldValue(selectedMember, 'name');
                  const roleTitle = getFieldValue(selectedMember, 'roleTitle');
                  const email = getFieldValue(selectedMember, 'email');
                  const preferredPronouns = getFieldValue(selectedMember, 'preferredPronouns');
                  const displayName = name ? name.toUpperCase() : '';
                  const pronounsDisplay = preferredPronouns
                    ? String(preferredPronouns).toLowerCase()
                    : '';
                  
                  // Get bio/description with robust locale handling
                  const bio =
                    extractRichTextFromFields(selectedMember, 'bio') ||
                    extractRichTextFromFields(selectedMember, 'description');
                  const renderedBio = bio ? renderRichText(bio) : null;
                  
                  return (
                    <>
                      <div className="overlay-member-info">
                        <h3 className="overlay-name">
                          {displayName}
                          {pronounsDisplay && (
                            <span className="overlay-pronouns"> ({pronounsDisplay})</span>
                          )}
                        </h3>
                        {roleTitle && <p className="overlay-title">{roleTitle}</p>}
                        {email && (
                          <a href={`mailto:${email}`} className="overlay-email">
                            {email}
                          </a>
                        )}
                      </div>
                      
                      {renderedBio && renderedBio.length > 0 && (
                        <div className="overlay-bio-section">
                          <div className="overlay-bio">
                            {renderedBio}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}

export default Committee;
